import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../campaigns/hooks/useToast'
import { LANGUAGES, TRADE_TYPES } from '../utils/campaignConstants'

export const useProfileManagement = (
  profiles,
  setProfiles,
  selectedProfileIndex,
  setSelectedProfileIndex,
  watch,
  reset,
  getLocalizedCountryName,
  userEditState
) => {
  const { t } = useTranslation()

  const { showError } = useToast()

  const [isAddingNewProfile, setIsAddingNewProfile] = useState(false)
  const [newProfileName, setNewProfileName] = useState('')
  const previousProfileBeforeAddRef = useRef(null)

  const saveCurrentProfileData = useCallback(() => {
    const currentValues = watch()
    const updatedProfiles = [...profiles]
    if (updatedProfiles[selectedProfileIndex]) {
      updatedProfiles[selectedProfileIndex] = {
        ...updatedProfiles[selectedProfileIndex],
        productName: currentValues.productName?.map(item => item.value || item) || [],
        productListOp: currentValues.productListOp || 'or',
        sender_company: {
          name: currentValues['senderCompany.name'] || '',
          industry: currentValues['senderCompany.industry'] || '',
          country: currentValues['senderCompany.country'] || '',
          contact_person: currentValues['senderCompany.contact_person'] || '',
          contact_title: currentValues['senderCompany.contact_title'] || '',
          email: currentValues['senderCompany.email'] || '',
          key_products: currentValues['senderCompany.key_products']?.map(item => typeof item === 'string' ? item : item.value) || [],
          website: currentValues['senderCompany.website'] || '',
          phone: currentValues['senderCompany.phone'] || '',
          production_capacity: currentValues['senderCompany.production_capacity'] || '',
          facilities: currentValues['senderCompany.facilities']?.map(item => typeof item === 'string' ? item : item.value) || [],
          certifications: currentValues['senderCompany.certifications']?.map(item => typeof item === 'string' ? item : item.value) || [],
          market_experience: currentValues['senderCompany.market_experience']?.map(item => typeof item === 'string' ? item : item.value) || [],
          company_values: currentValues['senderCompany.company_values']?.map(item => typeof item === 'string' ? item : item.value) || []
        },
        email_purpose: {
          target_market: currentValues['emailPurpose.target_market'] || '',
          trade_type: currentValues['emailPurpose.trade_type'] || TRADE_TYPES.EXPORT,
          product_category: currentValues['emailPurpose.product_category'] || '',
          language: currentValues['emailPurpose.language'] || LANGUAGES.ENGLISH,
          specific_products: currentValues['emailPurpose.specific_products']?.map(item => typeof item === 'string' ? item : item.value) || [],
          call_to_action: currentValues['emailPurpose.call_to_action'] || ''
        },
        emailSignatureFileId: currentValues.emailSignatureFileId || null
      }
      setProfiles(updatedProfiles)
    }
    return updatedProfiles
  }, [profiles, selectedProfileIndex, watch, setProfiles])

  const loadProfileData = useCallback((profile, currentValues) => {
    setTimeout(() => {
      reset({
        emailUserName: currentValues.emailUserName,
        emailPassword: currentValues.emailPassword,
        smtpHostName: currentValues.smtpHostName,
        smtpPort: currentValues.smtpPort,
        transportStrategy: currentValues.transportStrategy,
        rxHostName: currentValues.rxHostName,
        rxPort: currentValues.rxPort,
        rxProtocol: currentValues.rxProtocol,
        inboxFolderName: currentValues.inboxFolderName,
        productName: profile.productName || [],
        productListOp: profile.productListOp || 'or',
        'senderCompany.name': profile.sender_company?.name || '',
        'senderCompany.industry': profile.sender_company?.industry || '',
        'senderCompany.country': getLocalizedCountryName(profile.sender_company?.country || ''),
        'senderCompany.contact_person': profile.sender_company?.contact_person || '',
        'senderCompany.contact_title': profile.sender_company?.contact_title || '',
        'senderCompany.email': profile.sender_company?.email || '',
        'senderCompany.key_products': profile.sender_company?.key_products || [],
        'senderCompany.website': profile.sender_company?.website || '',
        'senderCompany.phone': profile.sender_company?.phone || '',
        'senderCompany.production_capacity': profile.sender_company?.production_capacity || '',
        'senderCompany.facilities': profile.sender_company?.facilities || [],
        'senderCompany.certifications': profile.sender_company?.certifications || [],
        'senderCompany.market_experience': profile.sender_company?.market_experience || [],
        'senderCompany.company_values': profile.sender_company?.company_values || [],
        'emailPurpose.target_market': getLocalizedCountryName(profile.email_purpose?.target_market || ''),
        'emailPurpose.trade_type': profile.email_purpose?.trade_type || TRADE_TYPES.EXPORT,
        'emailPurpose.product_category': profile.email_purpose?.product_category || '',
        'emailPurpose.language': profile.email_purpose?.language || LANGUAGES.ENGLISH,
        'emailPurpose.specific_products': profile.email_purpose?.specific_products || [],
        'emailPurpose.call_to_action': profile.email_purpose?.call_to_action || '',
        emailSignatureFileId: profile.emailSignatureFileId || ''
      })
    }, 0)
  }, [reset, getLocalizedCountryName])

  const handleProfileChange = useCallback((index) => {
    const updatedProfiles = saveCurrentProfileData()
    setSelectedProfileIndex(index)
    const selectedProfile = updatedProfiles[index]
    if (selectedProfile) {
      const currentValues = watch()
      loadProfileData(selectedProfile, currentValues)
    }
  }, [saveCurrentProfileData, watch, loadProfileData, setSelectedProfileIndex])

  const handleStartAddProfile = useCallback(() => {
    const updatedProfiles = saveCurrentProfileData()
    previousProfileBeforeAddRef.current = updatedProfiles[selectedProfileIndex]

    const currentValues = watch()
    setTimeout(() => {
      reset({
        emailUserName: currentValues.emailUserName,
        emailPassword: currentValues.emailPassword,
        smtpHostName: currentValues.smtpHostName,
        smtpPort: currentValues.smtpPort,
        transportStrategy: currentValues.transportStrategy,
        rxHostName: currentValues.rxHostName,
        rxPort: currentValues.rxPort,
        rxProtocol: currentValues.rxProtocol,
        inboxFolderName: currentValues.inboxFolderName,
        productName: [],
        productListOp: 'or',
        'senderCompany.name': '',
        'senderCompany.industry': '',
        'senderCompany.country': '',
        'senderCompany.contact_person': '',
        'senderCompany.contact_title': '',
        'senderCompany.email': userEditState?.emailSettings?.emailUserName || '',
        'senderCompany.key_products': [],
        'senderCompany.website': '',
        'senderCompany.phone': '',
        'senderCompany.production_capacity': '',
        'senderCompany.facilities': [],
        'senderCompany.certifications': [],
        'senderCompany.market_experience': [],
        'senderCompany.company_values': [],
        'emailPurpose.target_market': '',
        'emailPurpose.trade_type': TRADE_TYPES.EXPORT,
        'emailPurpose.product_category': '',
        'emailPurpose.language': LANGUAGES.ENGLISH,
        'emailPurpose.specific_products': [],
        'emailPurpose.call_to_action': '',
        emailSignatureFileId: ''
      })
    }, 0)

    setIsAddingNewProfile(true)
  }, [saveCurrentProfileData, selectedProfileIndex, watch, reset, userEditState])

  const handleProfileNameChange = useCallback((name) => {
    setNewProfileName(name)
  }, [])

  const handleAddNewProfile = useCallback((profileName) => {
    if (!profileName.trim()) {
      showError(t('mailTemplate.users.modal.profileNameRequired'))
      return
    }

    if (profiles.some(p => p.name === profileName.trim())) {
      showError(t('mailTemplate.users.modal.profileNameExists'))
      return
    }

    const currentValues = watch()
    const newProfile = {
      name: profileName.trim(),
      productName: currentValues.productName?.map(item => item.value || item) || [],
      productListOp: currentValues.productListOp || 'or',
      sender_company: {
        name: currentValues['senderCompany.name'] || '',
        industry: currentValues['senderCompany.industry'] || '',
        country: currentValues['senderCompany.country'] || '',
        contact_person: currentValues['senderCompany.contact_person'] || '',
        contact_title: currentValues['senderCompany.contact_title'] || '',
        email: currentValues['senderCompany.email'] || userEditState?.emailSettings?.emailUserName || '',
        key_products: currentValues['senderCompany.key_products']?.map(item => typeof item === 'string' ? item : item.value) || [],
        website: currentValues['senderCompany.website'] || '',
        phone: currentValues['senderCompany.phone'] || '',
        production_capacity: currentValues['senderCompany.production_capacity'] || '',
        facilities: currentValues['senderCompany.facilities']?.map(item => typeof item === 'string' ? item : item.value) || [],
        certifications: currentValues['senderCompany.certifications']?.map(item => typeof item === 'string' ? item : item.value) || [],
        market_experience: currentValues['senderCompany.market_experience']?.map(item => typeof item === 'string' ? item : item.value) || [],
        company_values: currentValues['senderCompany.company_values']?.map(item => typeof item === 'string' ? item : item.value) || []
      },
      email_purpose: {
        target_market: currentValues['emailPurpose.target_market'] || '',
        trade_type: currentValues['emailPurpose.trade_type'] || TRADE_TYPES.EXPORT,
        product_category: currentValues['emailPurpose.product_category'] || '',
        language: currentValues['emailPurpose.language'] || LANGUAGES.ENGLISH,
        specific_products: currentValues['emailPurpose.specific_products']?.map(item => typeof item === 'string' ? item : item.value) || [],
        call_to_action: currentValues['emailPurpose.call_to_action'] || ''
      },
      emailSignatureFileId: currentValues.emailSignatureFileId || null
    }

    setProfiles([...profiles, newProfile])
    setIsAddingNewProfile(false)
    setNewProfileName('')
    setSelectedProfileIndex(profiles.length)
    previousProfileBeforeAddRef.current = null
  }, [profiles, userEditState, showError, t, setProfiles, setSelectedProfileIndex, watch])

  const handleCancelAddProfile = useCallback(() => {
    setIsAddingNewProfile(false)
    setNewProfileName('')

    if (previousProfileBeforeAddRef.current !== null) {
      const previousProfile = previousProfileBeforeAddRef.current
      const currentValues = watch()
      loadProfileData(previousProfile, currentValues)
    }
    previousProfileBeforeAddRef.current = null
  }, [watch, loadProfileData])

  return {
    isAddingNewProfile,
    newProfileName,
    handleProfileChange,
    handleStartAddProfile,
    handleAddNewProfile,
    handleCancelAddProfile,
    handleProfileNameChange,
    saveCurrentProfileData
  }
}
